import React, { useEffect, useState } from 'react';
import { BuildingOfficeIcon, UserIcon, UsersIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { validateDocument, getValidationClass, getValidationMessage } from '../../utils/validators';
import { api } from '../../services/authService';

const SignersList = ({ contractData, selectedClient, signers, onChange }) => {
  // Track which signer CPF fields have been touched (for validation)
  const [cpfTouched, setCpfTouched] = useState({});
  const [locadorFallback, setLocadorFallback] = useState(null);

  // Buscar dados do locador do company_partners como fallback
  useEffect(() => {
    const loadLocadorFallback = async () => {
      try {
        const response = await api.get('/api/company-partners');
        const partners = response.data?.data || response.data || [];
        const primary = partners.find(p => p.is_primary) || partners[0];
        if (primary) {
          setLocadorFallback(primary);
        }
      } catch (err) {
        console.error('Erro ao buscar dados do locador:', err);
      }
    };
    loadLocadorFallback();
  }, []);

  // Preencher automaticamente os signatários quando os dados estiverem disponíveis
  useEffect(() => {
    if (contractData && selectedClient) {
      const autoSigners = [];

      // 1. LOCADOR (VIPLOC - Representante)
      const locadorNome = contractData.locador_representante_nome || locadorFallback?.name;
      const locadorCpf = contractData.locador_representante_cpf || locadorFallback?.cpf;
      const locadorEmail = contractData.locador_representante_email || locadorFallback?.email || '';
      const locadorCargo = contractData.locador_representante_cargo || locadorFallback?.position || 'Sócio Administrador';

      if (locadorNome && locadorCpf) {
        autoSigners.push({
          role: 'locador',
          name: locadorNome,
          email: locadorEmail,
          cpf: locadorCpf,
          position: locadorCargo,
          company: contractData.locador_razao_social || 'VIPLOC LOCAÇÕES LTDA',
          sign_as: 'party',
          required: true
        });
      }

      // 2. LOCATÁRIO — verificar primeiro se é procurador
      const signatarioTipo = contractData.signatario_tipo || 'socio';

      if (signatarioTipo === 'procurador' && contractData.locatario_representante_nome && contractData.locatario_representante_cpf) {
        // Procurador assina em nome da empresa
        autoSigners.push({
          role: 'locatario',
          name: contractData.locatario_representante_nome,
          email: selectedClient.email,
          cpf: contractData.locatario_representante_cpf,
          position: 'Procurador(a)',
          company: selectedClient.name,
          sign_as: 'party',
          required: true
        });
      } else {
        // Sócios — tentar múltiplas formas de acessar o array
        let socios = null;

        if (contractData.custom_data?.locatario_socios && Array.isArray(contractData.custom_data.locatario_socios)) {
          socios = contractData.custom_data.locatario_socios;
        } else if (contractData.locatario_socios && Array.isArray(contractData.locatario_socios)) {
          socios = contractData.locatario_socios;
        } else if (typeof contractData.custom_data === 'string') {
          try {
            const parsed = JSON.parse(contractData.custom_data);
            if (parsed.locatario_socios && Array.isArray(parsed.locatario_socios)) {
              socios = parsed.locatario_socios;
            }
          } catch (e) {
            console.error('Erro ao parsear custom_data:', e);
          }
        }

        if (socios && socios.length > 0) {
          // Filtrar apenas sócios marcados para assinar (assina !== false)
          const sociosSignatarios = socios.filter(s => s.assina !== false);
          sociosSignatarios.forEach((socio, index) => {
            autoSigners.push({
              role: index === 0 ? 'locatario' : `locatario${index + 1}`,
              name: socio.name,
              email: socio.email,
              cpf: socio.document,
              position: socio.legal_representative || 'Sócio',
              company: selectedClient.name,
              sign_as: 'party',
              required: true
            });
          });
        } else if (contractData.locatario_representante_nome && contractData.locatario_representante_cpf) {
          autoSigners.push({
            role: 'locatario',
            name: contractData.locatario_representante_nome,
            email: selectedClient.email,
            cpf: contractData.locatario_representante_cpf,
            position: contractData.locatario_representante_cargo || 'Representante Legal',
            company: selectedClient.name,
            sign_as: 'party',
            required: true
          });
        } else {
          autoSigners.push({
            role: 'locatario',
            name: selectedClient.name,
            email: selectedClient.email,
            cpf: selectedClient.document,
            position: 'Locatário',
            company: null,
            sign_as: 'party',
            required: true
          });
        }
      }

      // 3+ TESTEMUNHAS (dinâmico)
      const testemunhas = contractData.testemunhas || [];
      testemunhas.forEach((t, idx) => {
        if (t.nome && t.cpf) {
          autoSigners.push({
            role: `testemunha${idx + 1}`,
            name: t.nome,
            email: t.email || '',
            cpf: t.cpf,
            position: 'Testemunha',
            company: null,
            sign_as: 'witness',
            required: false
          });
        }
      });

      // Só atualiza se houver mudanças
      if (JSON.stringify(autoSigners) !== JSON.stringify(signers)) {
        onChange(autoSigners);
      }
    }
  }, [contractData, selectedClient, locadorFallback]);

  const handleSignerChange = (index, field, value) => {
    const updated = [...signers];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    onChange(updated);
  };

  const getRoleIcon = (role) => {
    if (role === 'locador') return <BuildingOfficeIcon className="w-5 h-5" />;
    if (role.startsWith('locatario')) return <UserIcon className="w-5 h-5" />;
    return <UsersIcon className="w-5 h-5" />;
  };

  const getRoleBadge = (role) => {
    // Locador
    if (role === 'locador') {
      return { text: 'LOCADOR', class: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' };
    }

    // Locatários (dinâmico: locatario, locatario2, locatario3, etc)
    if (role.startsWith('locatario')) {
      const number = role === 'locatario' ? '1' : role.replace('locatario', '');
      return { text: `LOCATÁRIO ${number}`, class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' };
    }

    // Testemunhas (dinâmico: testemunha1, testemunha2, testemunha3, etc)
    if (role.startsWith('testemunha')) {
      const number = role.replace('testemunha', '');
      return { text: `TESTEMUNHA ${number}`, class: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' };
    }

    // Default
    return { text: role.toUpperCase(), class: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300' };
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Signatários do Contrato
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Estas pessoas precisarão assinar o contrato digitalmente no Clicksign
        </p>
      </div>

      {/* Informativo */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex gap-3">
          <div className="text-blue-600 dark:text-blue-400 text-xl">ℹ️</div>
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium mb-1">Sobre as assinaturas:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-300">
              <li><strong>LOCADOR e LOCATÁRIO:</strong> Assinaturas obrigatórias (partes do contrato)</li>
              <li><strong>TESTEMUNHAS:</strong> Assinaturas opcionais (podem ser removidas)</li>
              <li><strong>Email:</strong> Necessário para receber link de assinatura do Clicksign</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Lista de Signatários */}
      <div className="space-y-3">
        {signers.map((signer, index) => {
          const roleBadge = getRoleBadge(signer.role);

          return (
            <div
              key={index}
              className={`
                border-2 rounded-lg p-4 transition-all
                ${signer.required
                  ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/10'
                  : 'border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/10'
                }
              `}
            >
              {/* Cabeçalho */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={signer.required ? 'text-blue-600 dark:text-blue-400' : 'text-purple-600 dark:text-purple-400'}>
                    {getRoleIcon(signer.role)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${roleBadge.class}`}>
                        {roleBadge.text}
                      </span>
                      {signer.required && (
                        <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded text-xs font-medium">
                          Obrigatório
                        </span>
                      )}
                    </div>
                    {signer.company && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        Representando: {signer.company}
                      </p>
                    )}
                  </div>
                </div>
                {signer.name && signer.email && signer.cpf && (
                  <CheckCircleIcon className="w-6 h-6 text-green-500" title="Dados completos" />
                )}
              </div>

              {/* Campos principais em uma linha */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nome Completo {signer.required && '*'}
                  </label>
                  <input
                    type="text"
                    value={signer.name}
                    onChange={(e) => handleSignerChange(index, 'name', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Nome completo"
                    required={signer.required}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email {signer.sign_as === 'witness' ? '' : signer.required && '*'}
                  </label>
                  <input
                    type="email"
                    value={signer.email}
                    onChange={(e) => handleSignerChange(index, 'email', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="email@exemplo.com"
                    required={signer.sign_as !== 'witness' && signer.required}
                  />
                  {signer.sign_as === 'witness' && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Para receber contrato no Clicksign
                    </p>
                  )}
                  {signer.required && signer.sign_as !== 'witness' && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Receberá link de assinatura do Clicksign
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    CPF {signer.required && '*'}
                  </label>
                  <input
                    type="text"
                    value={signer.cpf}
                    onChange={(e) => handleSignerChange(index, 'cpf', e.target.value)}
                    onBlur={() => setCpfTouched({ ...cpfTouched, [index]: true })}
                    className={`w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${getValidationClass(signer.cpf, cpfTouched[index]) || 'border-gray-300 dark:border-gray-600'}`}
                    placeholder="000.000.000-00"
                    required={signer.required}
                  />
                  {cpfTouched[index] && signer.cpf && (
                    <p className={`text-xs mt-1 ${getValidationMessage(signer.cpf, cpfTouched[index]).isValid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {getValidationMessage(signer.cpf, cpfTouched[index]).message}
                    </p>
                  )}
                </div>
              </div>

              {/* Cargo/Posição em linha separada (apenas para locador e locatário) */}
              {signer.sign_as === 'party' && (
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Cargo/Posição
                  </label>
                  <input
                    type="text"
                    value={signer.position}
                    onChange={(e) => handleSignerChange(index, 'position', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Ex: Sócio Administrador"
                  />
                </div>
              )}

              {/* Tipo de Assinatura */}
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">
                    Tipo de assinatura no Clicksign:
                  </span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {signer.sign_as === 'party' ? '📝 Parte do Contrato' : '👁️ Testemunha'}
                  </span>
                </div>
              </div>
            </div>
          );
        })}

        {signers.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p>Configure as partes do contrato na Etapa 1 para preencher automaticamente</p>
          </div>
        )}
      </div>

      {/* Resumo */}
      {signers.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                Resumo das Assinaturas
              </h4>
              <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <p>
                  📝 Partes do Contrato: {signers.filter(s => s.sign_as === 'party').length}
                </p>
                <p>
                  👁️ Testemunhas: {signers.filter(s => s.sign_as === 'witness').length}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {signers.length}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Total de signatários
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignersList;
